/**
 * Putting a site on a package by hand - the bridge that exists until every
 * account goes through Stripe checkout.
 *
 * Thin over `useAssignForm`: the fields, the detail fetch behind the effective
 * features and the members list, and the PATCH all live in the hook, so this
 * file is the CRM's dialog vocabulary and nothing else.
 */
import { type ReactNode } from "react";
import { Loader2 } from "lucide-react";
import {
  BILLING_CYCLES,
  DatePicker,
  SUBSCRIPTION_STATUSES,
  UNLIMITED,
  featureLabel,
  localeText,
  useAssignForm,
  type ApiClient,
  type Locale,
  type PlansT,
  type SubscriptionRow,
} from "@wa/ui";
import { useT } from "../../../i18n/I18nProvider";
import { Btn, MenuField, Modal, useToast } from "../../ui";
import { Field } from "../packages/_shared/LocaleFields";

/** A heading over a read-only block inside the dialog. Same label style as a
 *  field, but a div: there is no control under it to label. */
function Block({ title, children }: { title: string; children: ReactNode }) {
  return (
    <>
      <div className="crm-lab">{title}</div>
      {children}
    </>
  );
}

export function AssignModal({
  api,
  locale,
  row,
  planOptions,
  onClose,
  onSaved,
  t,
}: {
  api: ApiClient;
  locale: Locale;
  row: SubscriptionRow;
  planOptions: { code: string; name: string }[];
  onClose: () => void;
  onSaved: () => void;
  t: PlansT;
}) {
  const tc = useT();
  const toast = useToast();
  const {
    planCode, setPlanCode,
    status, setStatus,
    cycle, setCycle,
    periodEnd, setPeriodEnd,
    trialEnd, setTrialEnd,
    cancelAtEnd, setCancelAtEnd,
    detail, features, featureKeys,
    saving, save,
  } = useAssignForm({ api, locale, row, onSaved });

  // The hook rethrows a failed PATCH and the shared dialog had no catch at all,
  // so a rejected save just switched the button back on and looked like nothing
  // had happened. Here it says so.
  async function submit() {
    try {
      await save();
    } catch {
      toast(tc("common.error"));
    }
  }

  return (
    <Modal
      title={t("subs.assignFor")}
      label={t("subs.assignFor")}
      onClose={onClose}
      className="w-[min(720px,100%)]"
      footer={
        <>
          <Btn type="button" onClick={onClose} disabled={saving}>
            {t("cancel")}
          </Btn>
          <Btn type="button" tone="dark" disabled={saving} onClick={() => void submit()}>
            {saving ? <Loader2 className="size-4 animate-spin" /> : null}
            {t("save")}
          </Btn>
        </>
      }
    >
      <p className="crm-sub mt-1">
        {row.siteName} · {row.siteSlug}
      </p>

      <Field label={t("subs.field.plan")}>
        <MenuField
          label={t("subs.field.plan")}
          variant="form"
          value={planCode}
          onChange={setPlanCode}
          options={planOptions.map((p) => ({ value: p.code, label: p.name }))}
        />
      </Field>

      <div className="grid gap-3 sm:grid-cols-2">
        <div className="min-w-0">
          <Field label={t("subs.field.status")}>
            <MenuField
              label={t("subs.field.status")}
              variant="form"
              value={status}
              onChange={setStatus}
              options={SUBSCRIPTION_STATUSES.map((s) => ({ value: s, label: t(`status.${s}`) }))}
            />
          </Field>
        </div>
        <div className="min-w-0">
          <Field label={t("subs.field.cycle")}>
            <MenuField
              label={t("subs.field.cycle")}
              variant="form"
              value={cycle}
              onChange={setCycle}
              options={BILLING_CYCLES.map((c) => ({ value: c, label: t(`cycle.${c}`) }))}
            />
          </Field>
        </div>
        {/* The shared calendar rather than `<input type="date">`: the native one
            is a different control in every browser, and it was the only field
            in this dialog still drawing its own chrome. */}
        <div className="min-w-0">
          <Field label={t("subs.field.periodEnd")}>
            <DatePicker
              value={periodEnd}
              onChange={setPeriodEnd}
              locale={locale}
              placeholder={t("subs.field.periodEnd")}
            />
          </Field>
        </div>
        <div className="min-w-0">
          <Field label={t("subs.field.trialEnd")}>
            <DatePicker
              value={trialEnd}
              onChange={setTrialEnd}
              locale={locale}
              placeholder={t("subs.field.trialEnd")}
            />
          </Field>
        </div>
      </div>

      <label className="crm-inp mt-5 cursor-pointer">
        <input type="checkbox" checked={cancelAtEnd} onChange={(e) => setCancelAtEnd(e.target.checked)} />
        <span className="text-[13.5px]">{t("subs.field.cancelAtEnd")}</span>
      </label>

      {/* What the site can actually do right now - resolved from the package
          plus any add-ons, not read off the package alone. */}
      <Block title={t("subs.resolvedFeatures")}>
        {detail ? (
          featureKeys.length ? (
            <div className="flex flex-wrap gap-1.5">
              {featureKeys.map((k) => (
                <span key={k} className="crm-chip">
                  {featureLabel(k, locale)}
                  {typeof features[k] === "number" ? (
                    <span className="crm-tnum opacity-70">
                      {features[k] === UNLIMITED ? "∞" : String(features[k])}
                    </span>
                  ) : null}
                </span>
              ))}
            </div>
          ) : (
            <p className="crm-mut text-[13px]">{t("none")}</p>
          )
        ) : (
          <div className="flex justify-center py-4 text-muted-foreground">
            <Loader2 className="size-5 animate-spin" />
          </div>
        )}
      </Block>

      {detail && detail.members.length > 0 ? (
        <Block title={t("subs.members")}>
          <div className="flex flex-col gap-2">
            {detail.members.map((m) => (
              <div key={m.email} className="flex min-w-0 items-center gap-3 text-[13px]">
                <span className="crm-trunc">
                  {m.name} <span className="crm-mut">· {m.email}</span>
                </span>
                <span className="crm-chip crm-push shrink-0">{localeText(m.roleName, locale) || m.roleKey}</span>
              </div>
            ))}
          </div>
        </Block>
      ) : null}
    </Modal>
  );
}
